import React, { useState, useEffect } from 'react';
import { Course, Study, UserProfile, Organization } from '../types';
import { getCourseById, completeCourse, saveCourseProgress } from '../services/firebase';
import { Icons } from './ui/Icons';
import { AppHeader } from './ui/AppHeader';
import { useAuthModal } from '../contexts/AuthContext';

interface CourseViewerProps {
  courseId: string;
  user: UserProfile | null;
  organization: Organization | null;
  studies: Study[];
  onStartStudy: (studyId: string) => void;
  onClose: () => void;
  onLogout?: () => void;
}

export const CourseViewer: React.FC<CourseViewerProps> = ({
  courseId,
  user,
  organization,
  studies,
  onStartStudy,
  onClose,
  onLogout
}) => {
  const { openAuthModal } = useAuthModal();
  const [activeTab, setActiveTab] = useState('courses');
  const [course, setCourse] = useState<Course | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadCourse();
  }, [courseId]);

  const loadCourse = async () => {
    setIsLoading(true);
    try {
      const data = await getCourseById(courseId);
      setCourse(data);
    } catch (error) {
      console.error('Failed to load course:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getStudyIds = (): string[] => {
    return course?.studyIds || [];
  };

  const isStudyCompleted = (studyId: string): boolean => {
    return user?.completedStudyIds?.includes(studyId) ?? false;
  };

  const isStudyUnlocked = (index: number): boolean => {
    if (index === 0) return true;
    if (!course) return false;

    // If sequential viewing is not required, all studies are unlocked
    if (!course.requireSequentialViewing) return true;

    const studyIds = getStudyIds();
    const previousStudyId = studyIds[index - 1];
    return isStudyCompleted(previousStudyId);
  };

  const getStudy = (studyId: string): Study | undefined => {
    return studies.find(s => s.id === studyId);
  };

  const isCourseCompleted = (): boolean => {
    const studyIds = getStudyIds();
    if (studyIds.length === 0) return false;
    return studyIds.every(studyId => isStudyCompleted(studyId));
  };

  const getCompletedCount = (): number => {
    return getStudyIds().filter(studyId => isStudyCompleted(studyId)).length;
  };

  const getTotalStudyCount = (): number => {
    return getStudyIds().length;
  };

  const getProgressPercentage = (): number => {
    const total = getTotalStudyCount();
    if (total === 0) return 0;
    return (getCompletedCount() / total) * 100;
  };

  const handleStartStudy = async (studyId: string, index: number) => {
    if (!isStudyUnlocked(index)) {
      if (course?.requireSequentialViewing) {
        alert('This course requires sequential viewing. You must complete the previous study first!');
      } else {
        alert('You must complete the previous study first!');
      }
      return;
    }

    if (user) {
      await saveCourseProgress(user.uid, courseId, index);
    }
    onStartStudy(studyId);
  };

  useEffect(() => {
    if (user && course && isCourseCompleted() && !user.completedCourseIds?.includes(courseId)) {
      completeCourse(user.uid, courseId);
    }
  }, [course, user?.completedStudyIds]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Icons.Loader className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!course) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600 text-lg mb-4">Course not found</p>
          <button onClick={onClose} className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-blue-900">
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header with Navigation */}
      {user && (
        <AppHeader
          user={user}
          organization={organization}
          onLogout={onLogout || (() => {})}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
        />
      )}

      {/* Course Action Bar */}
      <div className="bg-white border-b px-6 py-3">
        <div className="max-w-7xl mx-auto">
          <button onClick={onClose} className="flex items-center gap-2 text-gray-600 hover:text-gray-900">
            <Icons.Prev className="w-5 h-5" />
            Back to Courses
          </button>
        </div>
      </div>

      {/* Cover Image */}
      {course.coverImage ? (
        <div
          className="h-64 bg-cover bg-center relative"
          style={{
            backgroundImage: `url(${course.coverImage})`,
            backgroundPosition: `${course.coverImagePosition?.x || 50}% ${course.coverImagePosition?.y || 50}%`
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent flex items-end">
            <div className="max-w-5xl mx-auto w-full px-6 py-8">
              <h1 className="text-4xl font-serif font-bold text-white mb-2">{course.title}</h1>
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-gradient-to-br from-indigo-600 to-purple-700 py-16">
          <div className="max-w-5xl mx-auto px-6">
            <h1 className="text-4xl font-serif font-bold text-white mb-2">{course.title}</h1>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="max-w-5xl mx-auto px-6 py-8">
        {/* Meta Info */}
        <div className="bg-white rounded-xl p-6 shadow-sm mb-6">
          <p className="text-gray-700 text-lg mb-4">{course.description}</p>

          <div className="flex items-center gap-6 text-sm text-gray-600 mb-4">
            <span className="flex items-center gap-2">
              <Icons.BookOpen className="w-4 h-4" />
              <strong>{getTotalStudyCount()}</strong> studies
            </span>
            <span className="flex items-center gap-2">
              <Icons.Check className="w-4 h-4" />
              <strong>{getCompletedCount()}</strong> completed
            </span>
            {course.requireSequentialViewing && (
              <span className="inline-flex items-center gap-1 bg-blue-100 text-blue-700 px-3 py-1 rounded-full font-medium">
                <span className="text-sm">🔒</span>
                Sequential
              </span>
            )}
            {isCourseCompleted() && (
              <span className="inline-flex items-center gap-1 bg-green-100 text-green-700 px-3 py-1 rounded-full font-medium">
                <Icons.Check className="w-4 h-4" />
                Completed
              </span>
            )}
          </div>

          {course.tags.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-4">
              {course.tags.map(tag => (
                <span key={tag} className="bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-sm">
                  {tag}
                </span>
              ))}
            </div>
          )}

          {/* Progress Bar */}
          <div>
            <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
              <span>Progress</span>
              <span className="font-semibold text-primary">{Math.round(getProgressPercentage())}%</span>
            </div>
            <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden">
              <div
                className={`h-full transition-all ${isCourseCompleted() ? 'bg-green-500' : 'bg-primary'}`}
                style={{ width: `${getProgressPercentage()}%` }}
              />
            </div>
          </div>
        </div>

        {/* Studies List */}
        <div className="space-y-3">
          <h3 className="text-xl font-semibold text-gray-900 mb-4">Studies</h3>

          {getStudyIds().map((studyId, index) => {
            const completed = isStudyCompleted(studyId);
            const unlocked = isStudyUnlocked(index);
            const study = getStudy(studyId);

            if (!study) {
              return (
                <div key={studyId} className="bg-orange-50 border border-orange-200 rounded-xl p-6">
                  <div className="flex items-start gap-4">
                    <div className="flex items-center justify-center w-10 h-10 bg-orange-200 text-orange-700 rounded-full font-bold flex-shrink-0">
                      {index + 1}
                    </div>
                    <div>
                      <h4 className="font-semibold text-orange-900">Study Unavailable</h4>
                      <p className="text-sm text-orange-700 mt-1">This study is no longer available</p>
                    </div>
                  </div>
                </div>
              );
            }

            return (
              <div
                key={studyId}
                className={`bg-white border-2 rounded-xl p-6 transition-all ${
                  completed
                    ? 'border-green-200 bg-green-50/30'
                    : unlocked
                    ? 'border-gray-200 hover:border-primary hover:shadow-md'
                    : 'border-gray-200 opacity-60'
                }`}
              >
                <div className="flex items-start gap-4">
                  <div
                    className={`flex items-center justify-center w-10 h-10 rounded-full font-bold flex-shrink-0 ${
                      completed
                        ? 'bg-green-500 text-white'
                        : unlocked
                        ? 'bg-primary text-white'
                        : 'bg-gray-300 text-gray-600'
                    }`}
                  >
                    {index + 1}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <h4 className="text-lg font-semibold text-gray-900 mb-1 flex items-center gap-2">
                          {study.title}
                          {completed && (
                            <span className="text-green-600">
                              <Icons.Check className="w-5 h-5" />
                            </span>
                          )}
                          {!unlocked && <span className="text-xl">🔒</span>}
                        </h4>
                        <p className="text-gray-600 text-sm mb-3">{study.description}</p>
                        <div className="flex items-center gap-4 text-xs text-gray-500">
                          <span>{study.segments.length} segments</span>
                          {study.tags.length > 0 && (
                            <>
                              <span>•</span>
                              <span>{study.tags.slice(0, 2).join(', ')}</span>
                            </>
                          )}
                        </div>
                      </div>

                      <div className="flex-shrink-0">
                        {!unlocked ? (
                          <button
                            disabled
                            className="px-6 py-2 bg-gray-300 text-gray-600 rounded-lg cursor-not-allowed"
                            title={course.requireSequentialViewing ? "Complete the previous study to unlock" : ""}
                          >
                            Locked
                          </button>
                        ) : completed ? (
                          <button
                            onClick={() => handleStartStudy(studyId, index)}
                            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                          >
                            Review
                          </button>
                        ) : (
                          <button
                            onClick={() => handleStartStudy(studyId, index)}
                            className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-blue-900"
                          >
                            {user?.studyProgress?.[studyId] !== undefined ? 'Continue' : 'Start'}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Completion Message */}
        {isCourseCompleted() && (
          <div className="mt-8 bg-gradient-to-br from-indigo-600 to-purple-700 rounded-xl p-8 text-center text-white">
            <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Icons.Award className="w-8 h-8" />
            </div>
            <h3 className="text-2xl font-bold mb-2">Congratulations!</h3>
            <p className="text-lg mb-1">You've completed all studies in this course!</p>
            <p className="text-yellow-300 font-semibold text-xl mt-4">+100 XP earned</p>
          </div>
        )}
      </div>
    </div>
  );
};
